/**
 * Mock `verifyGi` — runs the same chain of checks a Rust verifier
 * would, in the browser, using real Ed25519 + SHA-256. Accepts either
 * a parsed `GiBundle` (from a dropped `.gi` file) or an `EntityId` to
 * look up in the store.
 */

import type {
  EntityId,
  GiBundle,
  Hash256,
  PublicKey,
  SignedEntity,
  VerificationFinding,
  VerificationResult,
} from '../types';

import { appendActivity, getRecord, nowTimestamp, upsertRecord } from './store';
import { canonicalHash, canonicalJsonStringify } from './sign';
import { verifyEd25519 } from './spark';

const MOCK_LATENCY_MS = 180;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function verifyById(root_id: EntityId): Promise<VerificationResult | null> {
  await sleep(MOCK_LATENCY_MS);
  const rec = getRecord(root_id);
  if (!rec) return null;
  const bundle: GiBundle = {
    bundle: 'gi/v1',
    manifest: rec.manifest,
    signed_entity: rec.signed_entity,
    contract: rec.contract,
    lineage: rec.lineage,
  };
  return runVerification(bundle, { persist_counter: true });
}

export async function verifyBundle(bundle: GiBundle): Promise<VerificationResult> {
  await sleep(MOCK_LATENCY_MS);
  return runVerification(bundle, { persist_counter: true });
}

/** Parse arbitrary bytes into a `GiBundle`, throwing a typed error if
 *  the shape is wrong. Used by the Verify drop zone. */
export function parseBundleBytes(bytes: Uint8Array): GiBundle {
  const text = new TextDecoder().decode(bytes);
  const value = JSON.parse(text) as unknown;
  if (!isBundle(value)) {
    throw new BundleParseError('file does not look like a .gi bundle');
  }
  return value;
}

export class BundleParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BundleParseError';
  }
}

function isBundle(v: unknown): v is GiBundle {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Partial<GiBundle>;
  return (
    o.bundle === 'gi/v1' &&
    typeof o.manifest === 'object' &&
    typeof o.signed_entity === 'object' &&
    typeof o.contract === 'object' &&
    typeof o.lineage === 'object'
  );
}

// ─── Verification pipeline ────────────────────────────────────────

function runVerification(
  bundle: GiBundle,
  opts: { persist_counter: boolean },
): VerificationResult {
  const findings: VerificationFinding[] = [];

  // 1. Recompute manifest_hash and compare.
  const actual_manifest_hash = canonicalHash(bundle.manifest);
  if (!hashesEqual(actual_manifest_hash, bundle.signed_entity.manifest_hash)) {
    findings.push({
      kind: 'signature_invalid',
      reason: `manifest hash mismatch: signed entity claims ${bundle.signed_entity.manifest_hash}, manifest canonicalizes to ${actual_manifest_hash}`,
    });
  } else {
    // 2. Verify Ed25519 signature over the manifest hash.
    const sigOk = verifyEd25519Safe(
      bundle.signed_entity.signature,
      actual_manifest_hash,
      bundle.signed_entity.signer_public_key,
    );
    if (sigOk) {
      findings.push({ kind: 'signature_valid' });
    } else {
      findings.push({
        kind: 'signature_invalid',
        reason: 'Ed25519 signature did not verify against the manifest hash',
      });
    }
  }

  // 3. Contract hash consistency.
  const actual_contract_hash = canonicalHash(bundle.contract);
  if (hashesEqual(actual_contract_hash, bundle.manifest.contract_hash)) {
    findings.push({ kind: 'contract_hash_ok' });
  } else {
    findings.push({
      kind: 'contract_hash_mismatch',
      expected: bundle.manifest.contract_hash,
      actual: actual_contract_hash,
    });
  }

  // 4. Author / signer identity match (AD-015 in the Rust engine).
  if (bundle.manifest.author.public_key === bundle.signed_entity.signer_public_key) {
    // Fold into signature-valid; no separate finding (matches Rust).
  } else {
    findings.push({
      kind: 'signature_invalid',
      reason: 'manifest.author.public_key does not match signed_entity.signer_public_key',
    });
  }

  // 5. Lineage chain integrity.
  if (bundle.lineage.entries.length === 0) {
    findings.push({
      kind: 'chain_broken',
      at_index: 0,
      reason: 'lineage has no entries',
    });
  } else {
    let chain_ok = true;
    for (let i = 0; i < bundle.lineage.entries.length; i++) {
      const entry = bundle.lineage.entries[i]!;
      const prev = i === 0 ? null : bundle.lineage.entries[i - 1]!;
      const expected_parent = prev ? prev.entry_hash : null;
      if (entry.parent_hash !== expected_parent) {
        findings.push({
          kind: 'chain_broken',
          at_index: i,
          reason: `entry ${i} parent_hash does not match entry ${i - 1} hash`,
        });
        chain_ok = false;
        break;
      }
    }
    if (chain_ok) findings.push({ kind: 'chain_ok' });
  }

  // 6. Timestamp record.
  findings.push({ kind: 'timestamp_ok', at: bundle.signed_entity.created_at });

  // 7. Record the verification against the Vault counter if we have
  //    the originating record.
  if (opts.persist_counter) {
    const rec = getRecord(bundle.manifest.root_id);
    if (rec) {
      rec.counters.verifications += 1;
      upsertRecord(rec);
      // The activity log is updated at the handler layer — we do it
      // here so id- and bundle-path verifies both get logged.
      import_activity(bundle, findings);
    }
  }

  const ok = findings.every(
    (f) =>
      f.kind !== 'signature_invalid' &&
      f.kind !== 'chain_broken' &&
      f.kind !== 'contract_hash_mismatch' &&
      f.kind !== 'wasm_hash_mismatch' &&
      f.kind !== 'revoked_key',
  );

  return { ok, manifest: bundle.manifest, signed_entity: bundle.signed_entity, contract: bundle.contract, lineage: bundle.lineage, findings };
}

function hashesEqual(a: Hash256, b: Hash256): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

function verifyEd25519Safe(
  signature: SignedEntity['signature'],
  manifest_hash: Hash256,
  public_key: PublicKey,
): boolean {
  try {
    const sigHex = signature.slice('sig:'.length);
    const pkHex = public_key.slice('ed25519:'.length);
    const msg = hexToBytes(manifest_hash.slice('sha256:'.length));
    const sig = hexToBytes(sigHex);
    const pk = hexToBytes(pkHex);
    return verifyEd25519(sig, msg, pk);
  } catch {
    return false;
  }
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function import_activity(
  bundle: GiBundle,
  findings: VerificationFinding[],
) {
  const ok = findings.every(
    (f) =>
      f.kind !== 'signature_invalid' &&
      f.kind !== 'chain_broken' &&
      f.kind !== 'contract_hash_mismatch',
  );
  appendActivity({
    kind: 'verified',
    root_id: bundle.manifest.root_id,
    name: bundle.manifest.name,
    at: nowTimestamp(),
    ok,
  });
}

// Exported so spark.ts can reuse for round-trip sanity tests in future.
export const _canonicalJsonStringify = canonicalJsonStringify;
